import { Badge, Box, SimpleGrid, Text } from '@mantine/core';
import type { ReactElement } from 'react';
import StatBand from '@/design/StatBand';
import { SurfaceCard } from '@/design/SurfaceCard';
import type { BehavioralFriction, FrictionSession } from './InsightsTypes';

interface InsightsFrictionSectionProps {
    friction: BehavioralFriction;
}

function challengeDetail(session: FrictionSession): string {
    if (session.stuck) {
        return `${session.turns} prompts on lower-complexity work — a long back-and-forth worth restarting with a clearer, front-loaded ask.`;
    }
    return `${session.turns} prompts to resolve this thread — tightening the opening ask could cut the round-trips.`;
}

/** One high-friction session, mirroring the Highlights win card. */
function ChallengeCard({ session }: { session: FrictionSession }): ReactElement {
    // Friction is a negative signal — colour semantically (danger), not by a per-screen accent.
    return (
        <SurfaceCard $padding="md">
            <Box mb="sm">
                <Badge variant="light" style={{ background: 'var(--bm-color-danger-soft)', color: 'var(--bm-color-danger-text)', border: '1px solid var(--bm-color-danger-border)' }}>
                    {session.topicLabel}
                </Badge>
            </Box>
            <Text size="sm" c="dimmed" style={{ lineHeight: 1.55 }}>
                {challengeDetail(session)}
            </Text>
        </SurfaceCard>
    );
}

/** Challenges — the top few high-friction sessions worth revisiting (worst first). Mirrors the Highlights card grid. */
export default function InsightsFrictionSection({ friction }: InsightsFrictionSectionProps): ReactElement {
    const top = friction.sessions
        .slice()
        .sort((a, b) => Number(b.stuck) - Number(a.stuck) || b.turns - a.turns)
        .slice(0, 3);
    const longSessions = friction.sessions.filter((s) => s.stuck).length;
    return (
        <Box>
            <StatBand
                stats={[
                    {
                        label: 'Long sessions to revisit',
                        value: `${longSessions}`,
                        info: 'Sessions that ran long on lower-complexity work — the clearest candidates to restart with a tighter, front-loaded ask.',
                    },
                    {
                        label: 'Refinement loops',
                        value: `${friction.refinementLoops}`,
                        info: 'Times a prompt had to be re-asked or refined within a session — each loop adds overhead tokens.',
                    },
                    {
                        label: 'High-token turns',
                        value: `${friction.wasteIncidents}`,
                        info: 'Turns whose input context was oversized — trimming these recovers the most wasted tokens.',
                    },
                ]}
            />
            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md" mt="md">
                {top.map((session) => (
                    <ChallengeCard key={session.id} session={session} />
                ))}
            </SimpleGrid>
        </Box>
    );
}
